import { createContext, useContext, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { track } from './hooks/useAnalytics';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import Shell from './components/Shell';
import PresetManager from './components/PresetManager';
import BottomSheetPage from './components/layouts/BottomSheetPage';
import LoginPage from './components/LoginPage';
import LegalPage from './components/LegalPage';
import LandingPage from './components/LandingPage';
import PricingPage from './components/PricingPage';
import SupportPage from './components/SupportPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { theme, defaultLayout, PageLayout } from './themes';

export interface OpenTab {
  id: number;
  name: string;
}

const LayoutCtx = createContext<PageLayout>(defaultLayout);
export const usePageLayout = () => useContext(LayoutCtx);

// Sync flag so returning logged-in users still get a spinner instead of a flash.
const LOGGED_IN_KEY = 'airscribe_loggedIn';

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    track('page_view', { page_path: location.pathname });
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const { user, loading, isGuest } = useAuth();
  const location = useLocation();

  // On native (iOS/Android), never show the landing page — go straight to login or app
  const isNative = Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';

  // Public pages — no auth check needed, render immediately
  if (location.pathname === '/legal/tos') return <LegalPage page="tos" />;
  if (location.pathname === '/legal/privacy') return <LegalPage page="privacy" />;
  if (location.pathname === '/pricing') return <PricingPage />;
  if (location.pathname === '/support') return <SupportPage />;
  if (location.pathname === '/login') {
    if (!loading && user) return <Navigate to="/" replace />;
    return <LoginPage />;
  }

  // On native, show spinner while loading, then redirect to login if no user
  if (isNative) {
    if (loading) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!user && !isGuest) return <Navigate to="/login" replace />;
  }

  // Web: only show spinner if they were previously logged in (fixes LCP)
  const mightBeLoggedIn = localStorage.getItem(LOGGED_IN_KEY) === 'true';
  if (loading && mightBeLoggedIn) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user && !isGuest) return <LandingPage />;

  return (
    <LayoutCtx.Provider value={defaultLayout}>
      <Shell>
        <Routes>
          <Route path="/" element={<BottomSheetPage />} />
          <Route path="/presets" element={<PresetManager />} />
        </Routes>
      </Shell>
    </LayoutCtx.Provider>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <PageTracker />
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
